import React, { useEffect, useState } from "react";
import { API_URL } from "../lib/api";

/**
 * Loads the school logo. Source order:
 *   1. Backend-uploaded logo from /api/branding/logo  (with cache-bust ?v=)
 *   2. Static /dlshs-logo.png shipped with the public folder
 *   3. Inline SVG shield fallback ("DLS" mark)
 *
 * The cache-bust query string is changed when this component receives
 * a fresh `version` prop or when the page reloads.
 */
let SHARED_VERSION = Date.now();
const subscribers = new Set();

export function bumpLogoVersion() {
  SHARED_VERSION = Date.now();
  subscribers.forEach((cb) => cb(SHARED_VERSION));
}

export default function SchoolLogo({ size = 36, className = "" }) {
  const [version, setVersion] = useState(SHARED_VERSION);
  const [stage, setStage] = useState("api"); // 'api' | 'static' | 'svg'

  useEffect(() => {
    const cb = (v) => {
      setVersion(v);
      setStage("api"); // retry the API source after a (re)upload
    };
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  }, []);

  const px = `${size}px`;
  const apiSrc = `${API_URL}/branding/logo?v=${version}`;
  const staticSrc = "/dlshs-logo.png";

  return (
    <div
      className={`relative flex-shrink-0 ${className}`}
      style={{ width: px, height: px }}
    >
      {stage !== "svg" && (
        <img
          key={`${stage}-${version}`}
          src={stage === "api" ? apiSrc : staticSrc}
          alt="GANAP INC."
          width={size}
          height={size}
          className="w-full h-full object-contain"
          onError={() => setStage(stage === "api" ? "static" : "svg")}
        />
      )}
      {stage === "svg" && (
        <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <svg viewBox="0 0 64 64" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="dls-grad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#0A1F9E" />
                <stop offset="100%" stopColor="#030B45" />
              </linearGradient>
            </defs>
            <path
              d="M32 2 L58 10 V30 C58 46 46 56 32 62 C18 56 6 46 6 30 V10 Z"
              fill="url(#dls-grad)"
              stroke="#02083A"
              strokeWidth="1"
            />
            <text
              x="50%"
              y="56%"
              textAnchor="middle"
              fontFamily="Manrope, sans-serif"
              fontWeight="800"
              fontSize="20"
              fill="#FFFFFF"
              letterSpacing="1"
            >
              GI
            </text>
            <rect x="14" y="40" width="36" height="2" fill="#C9A227" />
          </svg>
        </div>
      )}
    </div>
  );
}
